import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import moment from "moment";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { environment } from '../../../environments/environment.dev';
import { IAttachment, IFile, IMilestone, IProject, IShareUser, IStatus, IUser } from "../../@core/models/model";
import { BaseService } from "../../@core/services/base.service";
import { IFormFilter } from "../components/document-form/document-form.component";
import { CONTRACT_GENERATION_STATUS, CONTRACT_TYPE, CUSTOMISATION_TONE, IContract, IContractSignatoryConf, IDocumentForm, IProjectDocument, SIGNATORY_TYPE, VERBOSITY } from "../models/contract-management.model";
import { IDocumentType } from "../models/settings.model";



export enum DOCUMENT_CREATION_SOURCE {
    DEFAULT = 'DEFAULT',
    STANDARD = 'STANDARD',
    CONTRACT_GENERATION = 'CONTRACT_GENERATION',
    RFP_GENERATION = 'RFP_GENERATION',
}


export interface IGenerateWithAIBody {
    prompt: string;
    docTypeId: string;
    template?: string;
    clauses?: string[];
    tone: string;
    dueDate?: string;
    customPrompt: string;
    linkedDocId?: string;
    referenceDocuments: { docModel: string; name: string; id: string, metadata?: Record<string, any> }[];
    project?: string;
    parentDocId?: string;
    shareUsers?: Partial<IShareUser>[];
    milestones?: Partial<IMilestone>[];
    source: DOCUMENT_CREATION_SOURCE;
}

interface IContractFilters {
    searchValue?: string,
    date?: Date
}

interface InsightDetails {
    inProgressJob: {
        username: string
    } | null,
    latestInsights: {
        username: string,
        updatedAt: Date,
        createdAt: Date,
    } | null
}

interface ICreateFormDocument {
    linkedDocType: string;
    file: File;
    project?: string;
    instructions?: string;
    knowledgeBase?: string[];
    dueDate?: Date;
    tone?: CUSTOMISATION_TONE;
    verbosity?: VERBOSITY;
    files?: string[];
    useQnaPair?: boolean;
    shareUsers?: Partial<IShareUser>[];
    milestones?: Partial<IMilestone>[];
}

interface IUpdateFormDocument {
    _id: string;
    instructions?: string;
    knowledgeBase?: string[];
    dueDate?: Date;
    tone?: CUSTOMISATION_TONE;
    verbosity?: VERBOSITY;
    files?: string[];
    useQnaPair?: boolean;
}

export interface IContractResponse extends IContract {
    assignees: Partial<IUser>[],
    documentStatus: IStatus,
    upcomingMilestone: Partial<IMilestone>[],
    dueDateExceeding?: boolean
}

@Injectable({ providedIn: 'root' })
export class ContractService extends BaseService {
    aiGeneratedContent = new BehaviorSubject<string>('');
    contractsUpdated = new Subject<void>();

    constructor(
        protected override http: HttpClient,
        protected ngZone: NgZone
    ) {
        super(http, ngZone);
    }


    apiUrl = environment.apiUrl + '/contract-management'

    checkIfDueDateExceeding(dueDate: Date | string) {
        return moment(dueDate).endOf('day').isBefore(moment().endOf('day'))
    }

    getSharedContracts() {
        return this.get<IContract[]>(this.apiUrl + '/shared-contracts')
    }

    getContractURL(id: string) {
        return this.get<{ url: string, key: string, attachment: IAttachment }>(this.apiUrl + '/contract-url/' + id);
    }

    getContracts(filters: { type: CONTRACT_TYPE, skip: number, limit: number, name: string, excludeDocs?: string[], includeDocs?: string[], projectId?: string, dueDate?: { from?: string, to?: string }, createdDate?: { from?: string, to?: string }, assignees?: string[], documentType?: string, documentStatus?: string, generationStatus?: CONTRACT_GENERATION_STATUS[], source?: DOCUMENT_CREATION_SOURCE }) {
        return this.post<{ contracts: IContractResponse[] | IProjectDocument[], total: number }>(this.apiUrl + '/contracts', filters)
    }


    getAiGeneratedContent() {
        return this.aiGeneratedContent.asObservable();
    }

    setAiGeneratedContent(content: string) {
        return this.aiGeneratedContent.next(content);
    }

    getContractRiskScore(contractId: string) {
        return this.get<number>(this.apiUrl + `/risk/${contractId}`)
    }

    getLatestInsightsDetails(contractId: string) {
        return this.get<InsightDetails>(this.apiUrl + `/latest-insight-details/${contractId}`)
    }

    shareContract(contractId: string, users: { user: IUser, role: string }[]) {
        return this.post<IContract>(this.apiUrl + '/share-contract/' + contractId, { data: users })
    }

    fetchContract(contractId: string) {
        return this.get<IContract & { containsForm: boolean, project?: IProject }>(this.apiUrl + '/fetch-contract/' + contractId)
    }

    checkGenWithAiInProgress(source: DOCUMENT_CREATION_SOURCE) {
        return this.get<boolean>(this.apiUrl + '/gen-with-ai-in-progress', { source })
    }

    generateContractWithAi(formData: FormData) {
        return this.post<void>(this.apiUrl + '/generate-with-ai', formData)
    }

    updateSignatories(contractId: string, signatories: { email: string, name: string }[]) {
        return this.post<IContract>(this.apiUrl + '/add-signatories/' + contractId, { signatories })
    }


    fetchContractSignatories(contractId: string) {
        return this.get<IContractSignatoryConf>(environment.apiUrl + '/signatory/fetch-contract-signatories/' + contractId)
    }

    deleteDocRole(contractId: string, user: IUser['_id']) {
        return this.delete<string>(this.apiUrl + '/remove-doc-role/' + contractId + '/' + user)
    }
    changeDocRole(contractId: string, user: IUser['_id'], role: string) {
        return this.post<IContract>(this.apiUrl + '/change-user-role/' + contractId + '/' + user, { role })
    }

    createContract(data: FormData) {
        return this.post<IContract>(this.apiUrl + '/create-contract', data)
    }

    updateContract(contractId: string, contract: IContract | Partial<IContract>): Observable<any> {
        return this.http.put(`${this.apiUrl}/edit-contract/${contractId}`, contract);
    }

    uploadContract(formData: FormData) {
        return this.post<IContract>(this.apiUrl + '/upload-contract', formData)
    }

    uploadMulitpleContract(formData: FormData) {
        return this.post<IContract>(this.apiUrl + '/upload-mulitple-contract', formData)
    }

    startSignatoryFlow(contractId: string, signatoryType: SIGNATORY_TYPE) {
        return this.post<IContract>(this.apiUrl + '/start-signatory-flow', {
            contractId,
            signatoryType
        })
    }

    resetSignatoryFlow(contractSignatoryConfId: string) {
        return this.post<IContract>(this.apiUrl + '/reset-signatory-flow/' + contractSignatoryConfId)
    }

    setDefaultSignatory(signatory: SIGNATORY_TYPE) {
        return this.post<IContract>(this.apiUrl + '/set-default-signatory', { signatory })
    }

    getDefaultSignatory() {
        return this.get<{
            defaultSignatory: SIGNATORY_TYPE,
        }>(environment.apiUrl + '/signatory/get-default-signatory')
    }

    downloadDocument(attachmentId: string) {
        return this.get<{ data: Uint8Array }>(this.apiUrl + '/download-signed-document/' + attachmentId)
    }

    downloadDocuments(documents: string[]) {
        return this.post<{ data: Uint8Array, type: string }>(this.apiUrl + '/download-documents', { documents })
    }

    deleteDocuments(documents: string[]) {
        return this.post(this.apiUrl + '/delete-documents', { documents })
    }

    downloadContract(contractId: string, type: string) {
        return this.post<{ data: Uint8Array }>(this.apiUrl + '/download-contract', { id: contractId, type: type })
    }

    fetchLastModifiedContract() {
        return this.get<any>(this.apiUrl + '/last-modified-contract')
    }

    deleteContract(contractId: string) {
        return this.delete<IContract>(this.apiUrl + '/delete-contract/' + contractId)
    }

    getDocumentsByType(type: string, filters: { search: string, skip: number, limit: number, excludeDocs: string[], projectId?: string }) {
        return this.post<{ contracts: IContract[], total: number }>(this.apiUrl + '/documents-by-type', { type, filters })
    }

    getCompareDocuments(filters: { document: string, compareDocuments: { _id: string, docModel: string, name: string }[], compareType: string }) {
        return this.post<{
            document: IContract & { summary: string },
            compareDocuments: (IContract | IFile & { summary: string } | IAttachment)[]
            // getCompareDocuments(filters: { document: string, compareDocuments: { _id: string, docModel: string, name: string }[], compareType: string, reqNewComparison?: boolean }) {
            //     return this.post<{
            //         result: {
            //             document: IContract & { summary: string },
            //             compareDocuments: (IContract | IFile & { summary: string })[]
            //         },
            //         comparison: {
            //             _id: string,
            //             name: string,
            //         }
        }>(this.apiUrl + '/compare-documents', filters)
    }
    getRelatedTemplates(prompt: string, docType: string, tone?: CUSTOMISATION_TONE | string) {
        return this.post<IFile[]>(this.apiUrl + '/get-prompt-related-templates', { prompt, docType, ...(tone ? { tone } : {}) })
    }

    createVersionsEmbeddings(versionIds: string[]) {
        return this.post<void>(this.apiUrl + '/create-versions-embeddings', { versionIds })
    }

    uploadFilesAndGenerateEmbeddings(files: File[]) {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        return this.post<{ name: string, _id: string }[]>(this.apiUrl + '/upload-files-and-generate-embeddings', formData)
    }

    isEmptyDoc(contractId: string) {
        return this.get<{
            contract: string,
            isEmpty: boolean
        }>(this.apiUrl + '/is-empty-doc/' + contractId)
    }


    // document that contains form
    createFormContract(data: ICreateFormDocument) {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) return;

            if (value instanceof Date) {
                formData.append(key, value.toISOString());
                return;
            }

            if (value instanceof File) {
                formData.append(key, value);
                return;
            }

            if (Array.isArray(value)) {
                if (value.length > 0 && value[0] instanceof File) {
                    (value as File[]).forEach((file) => formData.append(key, file));
                } else {
                    formData.append(key, JSON.stringify(value));
                }
                return;
            }

            if (typeof value === 'boolean') {
                formData.append(key, value.toString());
                return;
            }

            if (typeof value === 'object') {
                formData.append(key, JSON.stringify(value));
                return;
            }

            formData.append(key, String(value));
        });
        return this.post<IContract>(this.apiUrl + '/create-form-document', formData)
    }

    updateFormContract(data: IUpdateFormDocument) {
        return this.post<IContract>(this.apiUrl + '/update-form-document', data)
    }

    getDocumentForm(contractId: string, filter: IFormFilter = {}) {
        return this.post<IDocumentForm>(this.apiUrl + '/form-document/' + contractId, { filter })
    }

    fetchFileUrl(fileId: string) {
        return this.get<{ url: string, key: string, attachment: IAttachment }>(this.apiUrl + '/file-url/' + fileId)
    }

    reviewDocument(contractId: string, done: boolean = false) {
        return this.post<IContract>(this.apiUrl + '/review-document', { contractId, done })
    }

    linkToProject(contract: string, project: IProject['_id']) {
        return this.post<IContract>(this.apiUrl + '/link-project', { contract, project })
    }

    getCounts() {
        return this.post<{ documents: number, projects: number, archived: number }>(this.apiUrl + '/counts', { documentFilters: {}, projectFilters: {} })
    }

    logDownloadContract(contractId: string) {
        return this.post<void>(this.apiUrl + '/log-download-contract', { contractId })
    }
}
